/**
 * AgolR2Geocoder - Geocodificador AGOL R2 (36K+ features por municipio)
 *
 * Geocodifica infraestructuras usando datos ArcGIS Online desde Cloudflare R2.
 * Categorías: patrimonio, servicios, transporte, hidrografía,
 * infraestructuras energéticas, tejido económico, sistema urbano.
 *
 * Usa uFuzzy para matching difuso (400x más rápido que string-similarity).
 * Solo procesa features con geometría Point para coordenadas precisas.
 *
 * @module services/geocoding/specialized
 * @version 1.0.0
 * @date 2026-02-10
 */

import uFuzzy from '@leeoniya/ufuzzy';
import {
  getAGOLDataService,
  resetAGOLDataService,
  type AGOLFeature,
  type AGOLFilterOptions,
} from '../../data/AGOLDataService';

// =============================================================================
// CONSTANTES
// =============================================================================

/** Score mínimo para considerar un match válido */
const MIN_CONFIDENCE = 50;

/** Configuración de scoring según calidad del match */
const SCORE_CONFIG = {
  exact: 92,
  fuzzyHigh: 80,
  fuzzyMedium: 68,
  fuzzyLow: 58,
  municipioBonus: 3,
} as const;

/** Precisión estimada (metros) según tipo de match */
const PRECISION_MAP = {
  exact: 10,
  fuzzyHigh: 20,
  fuzzyMedium: 35,
  fuzzyLow: 50,
} as const;

// =============================================================================
// TIPOS
// =============================================================================

/** Opciones para geocodificación AGOL R2 */
export interface AgolR2GeocodingOptions {
  /** Nombre de la infraestructura a buscar */
  query: string;
  /** Código INE del municipio (5 dígitos) — requerido */
  codINE: string;
  /** Filtros opcionales de categoría/tipo AGOL */
  filter?: AGOLFilterOptions;
  /** Máximo de candidatos a evaluar (default 200) */
  maxCandidates?: number;
  /** Score mínimo uFuzzy (default 0.5) */
  minScore?: number;
}

/** Resultado de geocodificación AGOL R2 */
export interface AgolR2GeocodingResult {
  x: number;
  y: number;
  score: number;
  matchScore: number;
  source: 'agol_r2';
  confidence: 'high' | 'medium' | 'low';
  matchedName: string;
  matchDescription: string;
  precision: number;
  municipality?: string;
  province?: string;
  metadata: {
    tipo: string;
    categoria: string;
    categoriaNombre: string;
    capa: string;
    subcategoria: string | null;
  };
}

// =============================================================================
// UFUZZY INSTANCE
// =============================================================================

const fuzzyMatcher = new uFuzzy({
  intraMode: 1,
  intraIns: 2,
  intraSub: 1,
  intraTrn: 1,
  interIns: 3,
});

// =============================================================================
// CLASE PRINCIPAL
// =============================================================================

class AgolR2Geocoder {
  private static instance: AgolR2Geocoder | null = null;

  static getInstance(): AgolR2Geocoder {
    if (!AgolR2Geocoder.instance) {
      AgolR2Geocoder.instance = new AgolR2Geocoder();
    }
    return AgolR2Geocoder.instance;
  }

  static resetInstance(): void {
    AgolR2Geocoder.instance = null;
    resetAGOLDataService();
  }

  /**
   * Geocodifica una infraestructura usando datos AGOL R2.
   * Requiere codINE para scope municipal.
   */
  async geocode(options: AgolR2GeocodingOptions): Promise<AgolR2GeocodingResult | null> {
    const {
      query,
      codINE,
      filter,
      maxCandidates = 200,
      minScore = 0.5,
    } = options;

    if (!query || query.trim().length < 2 || !codINE) return null;

    const service = getAGOLDataService();

    let features: AGOLFeature[];
    try {
      features = await service.getFeatures(codINE, filter);
    } catch (err) {
      console.warn(`[AgolR2] Error cargando AGOL para ${codINE}:`, err);
      return null;
    }

    // Solo features Point con coordenadas válidas
    const pointFeatures = features
      .filter(f => f.geometry.type === 'Point' && Array.isArray(f.geometry.coordinates))
      .slice(0, maxCandidates);

    if (pointFeatures.length === 0) return null;

    // Haystack para uFuzzy
    const haystack = pointFeatures.map(f => f.properties.nombre ?? '');
    const normalizedQuery = query.trim().toUpperCase();

    const [idxs, info, order] = fuzzyMatcher.search(haystack, normalizedQuery);

    if (!idxs || idxs.length === 0 || !order || order.length === 0) return null;

    const bestIdx = order[0];
    const matchedFeatureIdx = idxs[bestIdx];
    const feature = pointFeatures[matchedFeatureIdx];
    const matchedName = haystack[matchedFeatureIdx];

    // Score basado en caracteres matcheados
    const charsMatched = info?.chars?.[bestIdx] ?? 0;
    const matchRatio = normalizedQuery.length > 0 ? charsMatched / normalizedQuery.length : 0;

    if (matchRatio < minScore) return null;

    const isExact = matchedName.toUpperCase() === normalizedQuery;
    let baseScore: number;
    let precision: number;

    if (isExact) {
      baseScore = SCORE_CONFIG.exact;
      precision = PRECISION_MAP.exact;
    } else if (matchRatio >= 0.8) {
      baseScore = SCORE_CONFIG.fuzzyHigh;
      precision = PRECISION_MAP.fuzzyHigh;
    } else if (matchRatio >= 0.6) {
      baseScore = SCORE_CONFIG.fuzzyMedium;
      precision = PRECISION_MAP.fuzzyMedium;
    } else {
      baseScore = SCORE_CONFIG.fuzzyLow;
      precision = PRECISION_MAP.fuzzyLow;
    }

    if (feature.properties.cod_mun === codINE) {
      baseScore += SCORE_CONFIG.municipioBonus;
    }

    const score = Math.min(100, Math.round(baseScore));
    if (score < MIN_CONFIDENCE) return null;

    const coords = feature.geometry.coordinates as number[];
    const confidenceLevel: 'high' | 'medium' | 'low' =
      score >= 85 ? 'high' : score >= 70 ? 'medium' : 'low';

    const quality = isExact ? 'exacto' : matchRatio >= 0.8 ? 'alto' : matchRatio >= 0.6 ? 'medio' : 'bajo';
    const desc = `${feature.properties.nombre} (${feature.properties.tipo}) en ${feature.properties.municipio} - match ${quality}`;

    return {
      x: coords[0],
      y: coords[1],
      score,
      matchScore: matchRatio,
      source: 'agol_r2',
      confidence: confidenceLevel,
      matchedName: feature.properties.nombre,
      matchDescription: desc,
      precision,
      municipality: feature.properties.municipio,
      province: feature.properties.provincia,
      metadata: {
        tipo: feature.properties.tipo,
        categoria: feature.properties.agol_categoria,
        categoriaNombre: feature.properties.agol_categoria_nombre,
        capa: feature.properties.agol_capa,
        subcategoria: feature.properties.agol_subcategoria,
      },
    };
  }
}

// =============================================================================
// EXPORTS
// =============================================================================

export function getAgolR2Geocoder(): AgolR2Geocoder {
  return AgolR2Geocoder.getInstance();
}

export function resetAgolR2Geocoder(): void {
  AgolR2Geocoder.resetInstance();
}

export { AgolR2Geocoder };

/**
 * Helper para compatibilidad con GeocodingOrchestrator
 */
export async function geocodeWithAgolR2(
  name: string,
  options: { codINE?: string; filter?: AGOLFilterOptions } = {}
): Promise<{
  x: number;
  y: number;
  confidence: number;
  source: string;
  matchedName: string;
  municipality?: string;
  province?: string;
} | null> {
  if (!options.codINE) return null;

  const geocoder = getAgolR2Geocoder();
  const result = await geocoder.geocode({
    query: name,
    codINE: options.codINE,
    filter: options.filter,
  });

  if (!result) return null;

  return {
    x: result.x,
    y: result.y,
    confidence: result.score,
    source: 'agol_r2',
    matchedName: result.matchedName,
    municipality: result.municipality,
    province: result.province,
  };
}
